import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import {createHashRouter, HashRouter, Route} from "react-router-dom";
import Backend from "./context/Backend";
import Usermanagement from "./components/usermanagement/Usermanagement";
import Login from "./components/login/Login";
import {redirect, RouterProvider, Routes} from "react-router";
import parse from 'url-parse';
import camelcaseKeys from "camelcase-keys";
import {Edit} from "./components/usermanagement/Edit";
import {New} from "./components/usermanagement/New";
import {Error} from "./components/usermanagement/Error";
import "./i18n"
import {Client} from "./api/Client";
import {MultiEdit} from "./components/usermanagement/MultiEdit";
import {Invite} from "./components/usermanagement/Invite";
import {ThemeProvider} from "react-bootstrap";
import {ErrorBoundary} from "react-error-boundary";
import {LIMIT} from "./constants";
import License from "./components/licensemanagement/License";
import LicensedUsers from "./components/licensedusers/LicensedUsers";
import DelegationsOverview from "./components/delegations/DelegationsOverview";
import Credits from "./components/usermanagement/Credits";

const client = new Client()

const router = createHashRouter(
    [
        {
            path: "/",
            element: <ErrorBoundary fallback={<Error/>} onError={handleError}><App /></ErrorBoundary>,
            id: "root",
            action: ({request, params}) => {
                return null; //posting forces loader revalidation
            },
            loader: async ({request, params}) => {

                const urlParams = new URL(request.url).searchParams;

                if (parse(request.url).pathname !== '/login') {
                    const currentUser = await new Promise((resolve) => {
                        client.apiGetUser(null, (response) => {
                            resolve(camelcaseKeys(response,{deep:true}));
                        }, (error) => {
                            resolve(redirect("/login"));
                        });
                    });

                    if (currentUser.status === 302) {
                        return currentUser;
                    }else {
                        const pathname = parse(request.url).pathname;
                        const hasUserManagement = currentUser.roles?.some(r => r.userManagement);
                        const hasLicenseManagement = currentUser.roles?.some(r => r.licenseManagement);

                        if ((pathname === '/licensed-users' || pathname === '/license') && !hasLicenseManagement) {
                            return redirect("/");
                        }

                        if (pathname === '/credits' && !hasUserManagement) {
                            return redirect("/");
                        }

                        //redirect on so-admin login to the page the user is authorized for
                        if (pathname === '/' && !hasUserManagement && hasLicenseManagement) {
                            return redirect("/licensed-users");
                        }

                        if (pathname === '/' && !hasUserManagement) {
                            return { usermanagement: Promise.resolve({ users: [], count: 0 }), currentUser };
                        }

                        let offset = ((urlParams.get("page")??1) - 1) * LIMIT;
                        let searchStr = urlParams.get("search");

                        if (searchStr) {
                            return {
                                usermanagement: new Promise((resolve) => {
                                        client.apiSearchUserManagementUsers({search:decodeURIComponent(searchStr)}, offset, LIMIT, (response) => {
                                            resolve(response)
                                        }, (error) => {
                                        })
                                    }
                                ),
                                currentUser: currentUser
                            };
                        }else {
                            return {
                                usermanagement: new Promise((resolve) => {
                                        client.apiGetUserManagementUsers(null, offset, LIMIT, (response) => {
                                            resolve(response)
                                        }, (error) => {
                                        })
                                    }
                                ),
                                currentUser: currentUser
                            };
                        }

                    }
                }else{
                    return {currentUser:null};
                }
            },
            children: [
                {
                    index: true,
                    element: <Usermanagement />
                },
                {
                    path: "/edit/:userId",
                    element: <Edit/>,
                    loader: async ({request,params}) => {
                        return await new Promise((resolve) => {
                            client.apiGetUserManagementUser({userId: params.userId}, (response) => {
                                resolve(camelcaseKeys(response))
                            }, (error) => {
                                resolve(redirect("/login"));
                            })
                        });
                    }
                },
                {
                    path: "/new",
                    element: <New />,
                },
                {
                    path: "/Invite",
                    element: <Invite />,
                },
                {
                    path: "/multiedit",
                    element: <MultiEdit />,
                },
                {
                    path: "/login",
                    element: <Login />
                },
                {
                    path: "/error",
                    element: <Error />,
                },
                {
                    path: "/license",
                    element: <License />
                },
                {
                    path: "/licensed-users",
                    element: <LicensedUsers />
                },
                {
                    path: "/delegations",
                    element: <DelegationsOverview />
                },
                {
                    path: "/credits",
                    element: <Credits />
                }
            ],
        },
    ]);


const root = ReactDOM.createRoot(document.getElementById('root'));

function handleError(error, info) {
    let errorStr;
    try{
        errorStr = error.toString()
    } catch (e){
        errorStr = "n/a"
    }
    fetch("/api/v1/usermanagement/log", {
        method: "POST",
        body: JSON.stringify({error: errorStr, info: info}),
        headers: {
            "Content-Type": "application/json",
        }})
}

root.render(
    <React.StrictMode>
            <Backend>
                <RouterProvider router={router}>
                    <ThemeProvider
                        breakpoints={['xxxl', 'xxl', 'xl', 'lg', 'md', 'sm', 'xs', 'xxs']}
                        minBreakpoint="xxs"
                    >
                        <App/>
                    </ThemeProvider>
                </RouterProvider>
            </Backend>
    </React.StrictMode>
);
